import * as Yup from "yup";
import { useFormik } from "formik";
import LoadingButton from "@mui/lab/LoadingButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Eye from "@untitled-ui/icons-react/build/esm/Eye";
import EyeOff from "@untitled-ui/icons-react/build/esm/EyeOff";

import { paths } from "src/paths";
import { useParams } from "react-router-dom";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { resetPassword } from "src/utils/snug/snugAuthApi";
import { useRouter } from "src/hooks/use-router";

interface Values {
  password: string;
  passwordConfirm: string;
}

const initialValues: Values = {
  password: "",
  passwordConfirm: "",
};

const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, "Must be at least 8 characters")
    .max(100, "Must be less than 100 characters")
    .required("Required"),
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Required"),
});

const Page = () => {
  const router = useRouter();
  let { token } = useParams();
  const [buttonText, setButtonText] = useState("Continue");
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmRef = useRef<HTMLInputElement>(null);

  // Toggle and then put focus back in the field. Browsers keep showing the
  // masked value of an unfocused password input after its type changes, so
  // without the refocus the eye appears to do nothing until the user clicks
  // into the box.
  const toggleShowPassword = (ref: React.RefObject<HTMLInputElement>) => {
    setShowPassword((v) => !v);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      try {
        el.setSelectionRange(end, end);
      } catch {
        // type=password does not support selection in some browsers
      }
    });
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        await resetPassword(token || "", values.password);
        toast.success("Password reset successfully. Redirecting to login...");
        setButtonText("Automatically redirecting");
        setTimeout(() => {
          router.push(paths.auth.jwt.entryStart);
        }, 2000);
      } catch (error) {
        setButtonText("Continue");
        toast.error("Error resetting password. Please try again.");
        helpers.setSubmitting(false);
        helpers.setErrors(error.response.data);
      }
    },
  });

  return (
    <>
      {/* <Seo title="Pro - Reset Password" /> */}
      <div>
      <Card elevation={16} sx={{borderRadius:1, p:3, px:4, pb:4}}>
          <CardHeader
            sx={{ pb: 0 }}
            title={<Typography variant="h5">Reset your password</Typography>}
          />
          <CardContent>
        
        <form noValidate onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <TextField
              error={!!(formik.touched.password && formik.errors.password)}
              fullWidth
              helperText={formik.touched.password && formik.errors.password}
              label="Password"
              name="password"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              inputRef={passwordRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggleShowPassword(passwordRef)}
                      edge="end"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              error={
                !!(
                  formik.touched.passwordConfirm &&
                  formik.errors.passwordConfirm
                )
              }
              fullWidth
              helperText={
                formik.touched.passwordConfirm && formik.errors.passwordConfirm
              }
              label="Password (Confirm)"
              name="passwordConfirm"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showPassword ? "text" : "password"}
              value={formik.values.passwordConfirm}
              inputRef={passwordConfirmRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggleShowPassword(passwordConfirmRef)}
                      edge="end"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <LoadingButton
            fullWidth
            size="large"
            sx={{ mt: 4 }}
            type="submit"
            variant="contained"
            loading={formik.isSubmitting}
          >
            {buttonText}
          </LoadingButton>
        </form>
        </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Page;
